import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Cookie, FileText } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { SITE, breadcrumbForPage } from "@/lib/seo";
import { cn } from "@/lib/utils";

type Section = {
  id: string;
  num: string;
  title: string;
  html: string;
};

const sections: Section[] = [
  {
    id: "que-son",
    num: "1",
    title: "¿Qué son las cookies?",
    html: `<p>Las cookies son pequeños archivos que se descargan en tu dispositivo al visitar una web y que permiten almacenar y recuperar información sobre tu navegación: recordar preferencias, medir visitas o medir la eficacia de la publicidad. Tecnologías similares (píxeles, etiquetas, almacenamiento local) reciben aquí el mismo tratamiento.</p>`,
  },
  {
    id: "tipos",
    num: "2",
    title: "¿Qué cookies usa esta web?",
    html: `<h3 class="text-base font-semibold text-foreground mt-2 mb-2">Cookies técnicas (necesarias) — no requieren consentimiento</h3>
<p>Imprescindibles para que la web funcione y para recordar tu elección sobre las demás cookies.</p>
<div class="overflow-x-auto mt-3">
<table class="w-full text-sm border border-border/60 rounded-lg overflow-hidden">
<thead class="bg-muted/50 text-foreground"><tr><th class="text-left p-2 font-semibold">Cookie</th><th class="text-left p-2 font-semibold">Proveedor</th><th class="text-left p-2 font-semibold">Finalidad</th><th class="text-left p-2 font-semibold">Duración</th></tr></thead>
<tbody>
<tr class="border-t border-border/60"><td class="p-2">Cookie de preferencias de consentimiento</td><td class="p-2">Vibra Marketing</td><td class="p-2">Recordar tu elección sobre las cookies</td><td class="p-2">12 meses</td></tr>
<tr class="border-t border-border/60"><td class="p-2">Cookies de sesión</td><td class="p-2">Vibra Marketing</td><td class="p-2">Funcionamiento básico de la web</td><td class="p-2">Sesión</td></tr>
</tbody>
</table>
</div>
<p class="mt-3">El pago de nuestros servicios se realiza en una página segura de Stripe externa a esta web; las cookies que Stripe utilice en su propia página se rigen por su política de cookies.</p>

<h3 class="text-base font-semibold text-foreground mt-5 mb-2">Cookies analíticas — requieren tu consentimiento</h3>
<p>Nos permiten medir cómo se usa la web (visitas, páginas vistas, origen del tráfico) para mejorarla.</p>
<div class="overflow-x-auto mt-3">
<table class="w-full text-sm border border-border/60 rounded-lg overflow-hidden">
<thead class="bg-muted/50 text-foreground"><tr><th class="text-left p-2 font-semibold">Cookie</th><th class="text-left p-2 font-semibold">Proveedor</th><th class="text-left p-2 font-semibold">Finalidad</th><th class="text-left p-2 font-semibold">Duración</th></tr></thead>
<tbody>
<tr class="border-t border-border/60"><td class="p-2">_ga / _ga_*</td><td class="p-2">Google Analytics 4</td><td class="p-2">Estadísticas de uso de la web</td><td class="p-2">24 meses</td></tr>
</tbody>
</table>
</div>

<h3 class="text-base font-semibold text-foreground mt-5 mb-2">Cookies de marketing — requieren tu consentimiento</h3>
<p>Permiten medir la eficacia de nuestras campañas publicitarias y mostrarte publicidad relevante en otras plataformas.</p>
<div class="overflow-x-auto mt-3">
<table class="w-full text-sm border border-border/60 rounded-lg overflow-hidden">
<thead class="bg-muted/50 text-foreground"><tr><th class="text-left p-2 font-semibold">Cookie</th><th class="text-left p-2 font-semibold">Proveedor</th><th class="text-left p-2 font-semibold">Finalidad</th><th class="text-left p-2 font-semibold">Duración</th></tr></thead>
<tbody>
<tr class="border-t border-border/60"><td class="p-2">_fbp</td><td class="p-2">Meta (Facebook/Instagram)</td><td class="p-2">Medición de campañas y publicidad</td><td class="p-2">3 meses</td></tr>
<tr class="border-t border-border/60"><td class="p-2">_gcl_au</td><td class="p-2">Google Ads</td><td class="p-2">Medición de conversiones</td><td class="p-2">3 meses</td></tr>
<tr class="border-t border-border/60"><td class="p-2">_ttp / _ttclid</td><td class="p-2">TikTok</td><td class="p-2">Medición de campañas y publicidad</td><td class="p-2">13 meses</td></tr>
</tbody>
</table>
</div>
<p class="text-xs mt-3 italic">Las cookies concretas pueden variar según las versiones de los scripts; esta lista se revisa periódicamente.</p>`,
  },
  {
    id: "quien",
    num: "3",
    title: "¿Quién usa la información de las cookies?",
    html: `<p>Las cookies propias las usa únicamente Vibra Marketing. Las de terceros (Google, Meta, TikTok) las usan dichos proveedores conforme a sus propias políticas de privacidad. Algunos están ubicados fuera del Espacio Económico Europeo; las transferencias se amparan en decisiones de adecuación (Marco de Privacidad de Datos UE-EE. UU.) o cláusulas contractuales tipo.</p>`,
  },
  {
    id: "configurar",
    num: "4",
    title: "¿Cómo aceptar, rechazar o configurar las cookies?",
    html: `<p>Al entrar en la web aparece un banner donde puedes <strong>aceptar todas, rechazar todas o configurarlas por categorías</strong>. Las cookies analíticas y de marketing <strong>solo se instalan si las aceptas</strong>: hasta entonces, sus scripts permanecen bloqueados.</p>
<p class="mt-3">Puedes cambiar tu decisión en cualquier momento desde el enlace <strong>"Configurar cookies"</strong> del pie de página de la web.</p>
<p class="mt-3">También puedes eliminar o bloquear cookies desde tu navegador:</p>
<ul class="list-disc pl-5 space-y-1 mt-2">
<li><strong>Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
<li><strong>Safari:</strong> Preferencias → Privacidad</li>
<li><strong>Firefox:</strong> Ajustes → Privacidad y seguridad</li>
<li><strong>Edge:</strong> Configuración → Cookies y permisos del sitio</li>
</ul>
<p class="mt-3">Ten en cuenta que bloquear las cookies técnicas puede afectar al funcionamiento de la web.</p>`,
  },
  {
    id: "mas-info",
    num: "5",
    title: "Más información",
    html: `<p>El tratamiento de los datos personales obtenidos a través de cookies se rige por nuestra Política de Privacidad. Para cualquier duda, escríbenos a <!--email_off--><a href="mailto:direccion@vibramarketing.es" class="text-primary hover:underline">direccion@vibramarketing.es</a><!--/email_off-->.</p>`,
  },
  {
    id: "actualizaciones",
    num: "6",
    title: "Actualizaciones",
    html: `<p>Esta política puede actualizarse para reflejar cambios en las cookies utilizadas o en la normativa. La versión vigente estará siempre publicada en esta página.</p>`,
  },
];

export default function CookiesPage() {
  const [active, setActive] = useState<string>(sections[0].id);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="es" />
        <title>Política de Cookies | Vibra Marketing</title>
        <meta name="description" content="Política de cookies de Vibra Marketing: tipos de cookies utilizadas, finalidad, duración y cómo aceptar, rechazar o configurarlas." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE.url}/cookies`} />
        <link rel="alternate" hrefLang="es-ES" href={`${SITE.url}/cookies`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content="Política de Cookies | Vibra Marketing" />
        <meta property="og:description" content="Tipos de cookies que usamos, finalidad, duración y cómo gestionarlas." />
        <meta property="og:url" content={`${SITE.url}/cookies`} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbForPage([
          { name: "Inicio", path: "/" },
          { name: "Política de cookies", path: "/cookies" },
        ]))}</script>
      </Helmet>
      <Header />

      <main className="relative overflow-hidden pt-28 pb-24">
        {/* Purple aura background */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-[520px] -z-10 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent" />
          <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-primary/25 blur-[120px]" />
        </div>

        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-5">
              <Cookie size={14} />
              Legal
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] mb-5">
              Política de <span className="text-gradient">cookies</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Titular: Vibra Marketing S.L. · CIF B25913948 · Calle Remolino 49B, 1ºE, 28710 El Molar (Madrid) · <span dangerouslySetInnerHTML={{ __html: '<!--email_off-->direccion@vibramarketing.es<!--/email_off-->' }} />
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Última actualización: <time dateTime="2026-06-12">12/06/2026</time>
            </p>
          </div>

          <div className="grid w-full min-w-0 lg:grid-cols-[260px_minmax(0,1fr)] gap-10 max-w-6xl mx-auto">
            {/* Sidebar nav */}
            <aside className="min-w-0 max-w-full lg:sticky lg:top-24 lg:self-start overflow-hidden">
              <nav className="flex min-w-0 max-w-full lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {sections.map((s) => {
                  const isActive = active === s.id;
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActive(s.id);
                        document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className={cn(
                        "shrink-0 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left whitespace-nowrap lg:whitespace-normal",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                          : "text-muted-foreground hover:text-foreground hover:bg-card"
                      )}
                    >
                      <span className="text-[11px] font-bold opacity-70">{s.num}</span>
                      <span>{s.title}</span>
                    </a>
                  );
                })}
              </nav>
            </aside>

            {/* Content */}
            <div className="min-w-0 space-y-8">
              {sections.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  data-reveal
                  className="scroll-mt-28 min-w-0 max-w-full rounded-2xl bg-card border border-border/60 p-5 sm:p-6 md:p-8 transition-all overflow-hidden"
                  onMouseEnter={() => setActive(s.id)}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-semibold text-primary/80 uppercase tracking-wider">Apartado {s.num}</span>
                      <h2 className="text-xl font-bold tracking-tight">{s.title}</h2>
                    </div>
                  </div>
                  <div
                    className="min-w-0 text-muted-foreground leading-relaxed space-y-2 break-words [&_a]:break-words"
                    dangerouslySetInnerHTML={{ __html: s.html }}
                  />
                </section>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
